import React, { useState, useRef, useEffect } from "react";
import WebCome from "../../components/WebCome";
import Topbar from "../../components/Topbar";
import Bottombar from "../../components/Bottombar";
import TranslateHeader from "../../components/Header/TranslateHeader";
import { ReactComponent as SelectorIcon } from "../../assets/svgs/selector.svg";
import { WEBSOCKET_BASE_URL } from "../../config";
import SpeakingIndicator from "./speakerIndicator";
import { toast } from "sonner";


function SignLanguageToTextSpeech() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("Sign language to Speech");
  const options = [
    { label: "Sign language to text", link: "/signlanguagetotext" },
    { label: "Text to sign language", link: "/textandspeechtosignlanguage" },
    { label: "Speech to Sign Language", link: "/SpeachToSignLanguage" },
  ];

  const toggleDropdown = () => setIsOpen(!isOpen);
  const [spokenIndices, setSpokenIndices] = useState([]);
  const speak = (text) => {
    if ("speechSynthesis" in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      window.speechSynthesis.speak(utterance);
    } else {
      console.warn("Speech Synthesis not supported in this browser.");
    }
  };
  

  const handleOptionClick = (option) => {
    setSelectedOption(option.label);
    setIsOpen(false);
    // Navigate to the selected option's link
    window.location.href = option.link;
  };

  const [camera, setCamera] = useState(false);
  const [handlebg, setHandleBg] = useState(false);
  const handleBackground = () => {
    setHandleBg(!handlebg);
    setCamera(!camera);
  };

  const [recording, setRecording] = useState(false);
  const [videoURL, setVideoURL] = useState(null);
  const [transcription, setTranscription] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [isSuccess, setIsSuccess] = useState(false);
  
  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunks = useRef([]);
  const websocketRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const frameIntervalRef = useRef(null);

  // Close WebSocket connection on component unmount
  useEffect(() => {
    return () => {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.close();
      }
      
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
      }
      
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const initWebSocket = () => {
    websocketRef.current = new WebSocket(`${WEBSOCKET_BASE_URL}/ws/translation/video-to-text`);
    
    websocketRef.current.onopen = () => {
      console.log("WebSocket connection established");
    };
    
    websocketRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.status === "processing") {
        setIsProcessing(true);
      } 
      else if (data.status === "done") {
        setIsProcessing(false);
        setIsSuccess(true);
        setTranscription((prevTranscription) => [
          ...prevTranscription,
          data.result,
        ]);
      } 
      else if (data.error) {
        setIsProcessing(false);
        setError(data.error);
      }
    };
    
    websocketRef.current.onerror = (error) => {
      console.error("WebSocket error:", error);
      setError("Connection error. Please try again.");
      setIsProcessing(false);
    };
    
    websocketRef.current.onclose = () => {
      console.log("WebSocket connection closed");
    };

  };

  const startRecording = async () => {
    try {
      setVideoURL(null);
      setTranscription([]);
      setError(null);
      setIsSuccess(false);
      recordedChunks.current = [];
  
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;
      mediaStreamRef.current = stream;
  
      initWebSocket();
  
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "video/webm",
      });
  
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunks.current.push(event.data);
        }
      };
  
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(recordedChunks.current, {
          type: "video/webm",
        });
        const url = URL.createObjectURL(blob);
        setVideoURL(url);
      };
  
      mediaRecorderRef.current.start();
      setRecording(true);
  
      const videoElement = videoRef.current;
  
      // Wait for metadata to be loaded
      videoElement.onloadedmetadata = () => {
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
  
        // Now dimensions will be available
        canvas.width = videoElement.videoWidth;
        canvas.height = videoElement.videoHeight;
  
        frameIntervalRef.current = setInterval(() => {
          if (
            websocketRef.current &&
            websocketRef.current.readyState === WebSocket.OPEN
          ) {
            try {
              context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
              canvas.toBlob(
                (blob) => {
                  if (blob) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      // const base64data = reader.result;
                      // console.log("✅ Base64 image:", base64data);
  
                      const arrayBuffer = reader.result.split(",")[1];
                      const byteCharacters = atob(arrayBuffer);
                      const byteNumbers = new Array(byteCharacters.length);
                      for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                      }
                      const byteArray = new Uint8Array(byteNumbers);
                      websocketRef.current.send(byteArray);
                    };
                    reader.readAsDataURL(blob);
                  }
                },
                "image/jpeg",
                0.8
              );
            } catch (error) {
              console.error("Error capturing frame:", error);
            }
          }
        }, 1000); // 1 frame per second
      };
    } catch (error) {
      console.error("Error starting recording:", error);
      setError("Failed to access camera. Please check permissions and try again.");
    }
  };
  

  const stopRecording = () => {
    // Stop sending frames
    if (frameIntervalRef.current) {
      clearInterval(frameIntervalRef.current);
      frameIntervalRef.current = null;
    }
    
    // Close WebSocket connection
    if (websocketRef.current) {
      websocketRef.current.close();
      websocketRef.current = null;
    }
    
    // Stop media recorder and tracks
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
    }
    
    setRecording(false);
  };

  const handleNewSubmission = () => {
    setVideoURL(null);
    setTranscription([]);
    setError(null);
    setIsSuccess(false);
    recordedChunks.current = [];
  };
  useEffect(() => {
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      const permissionCheck = new SpeechSynthesisUtterance("Speech synthesis enabled");
      // This will ask for microphone/speaker access in some browsers
      window.speechSynthesis.speak(permissionCheck);
      toast.success("Speech synthesis is ready. You can now listen to the translated text.");
    }
  }, []);

  const handleRespeak = () => {
    const synth = window.speechSynthesis;
    synth.cancel(); 
  
    const utterance = new SpeechSynthesisUtterance(
      transcription.map(item => item.prediction).join(" ")
    );
  
    synth.speak(utterance);
  };
  

  useEffect(() => {
    if (Array.isArray(transcription)) {
      transcription.forEach((item, index) => {
        if (!spokenIndices.includes(index)) {
          speak(item.prediction);
          setSpokenIndices((prev) => [...prev, index]);
        }
      });
    }
  }, [transcription]);

  // const mockWords = [
  //   "Letter A", "Letter B", "Letter C", "Letter D", "Letter G", "Letter H", "Letter J", "Letter H", "Letter Y", "Letter X", "Letter Z", "Letter k",
  // ];

  // useEffect(() => {

  //   let index = 0;
  //   setTranscription([]);

  //   const interval = setInterval(() => {
  //     if (index >= mockWords.length) {
  //       clearInterval(interval);
  //       return;
  //     }

  //     setTranscription((prev) => [...prev, { prediction: mockWords[index++] }]);
  //   }, 1000); 

  //   return () => clearInterval(interval);
  // }, []);

  return (
    <div className="overflow-auto sm:overflow-hidden">
      <div className="sm:hidden">
        <Topbar />
      </div>

      <div className="md:block hidden">
        <TranslateHeader />
      </div>

      <div className="items-center flex flex-col mt-[3rem] pt-4 sm:hidden">
        <div className="relative inline-block text-left z-40 bg-[#D9D9D980] rounded-[25px]">
          <div
            onClick={toggleDropdown}
            className="w-[310px] h-14 items-center flex cursor-pointer justify-end bg-[#2A71E8] rounded-[25px] top-5"
          >
            <div className="items-center space-x-10 flex px-10">
              <p className="text-white font-bold">{selectedOption}</p>
              <SelectorIcon />
            </div>
          </div>

          {isOpen && (
            <div className="absolute right-0 mt-2 w-[310px] ring-1 ring-black ring-opacity-0 space-y-10 h-[56px] mr-6">
              <div className="py-1 space-y-2 rounded-[25px] items-center px-5">
                {options.map((option) => (
                  <button
                    key={option.label}
                    onClick={() => handleOptionClick(option)}
                    className="w-[310px] bg-[#D9D9D9] px-4 py-2 rounded-[25px] h-10 items-center justify-center flex space-y-5 text-left text-gray-700 hover:bg-gray-100 focus:outline-none focus:bg-gray-100"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sm:flex sm:mt-[3rem] h-[100vh] sm:space-x-5 sm:mb-3 mt-[1px] sm:px-10 items-start ml-8 sm:ml-0">
        <div className={`${camera ? "" : "hidden"}`}>
          <WebCome
            handleBackground={handleBackground}
            startRecording={startRecording}
            stopRecording={stopRecording}
            isRecording={recording}
            videoRef={videoRef}
          />
        </div>

        {(
          <div
            className={`relative bg-black w-full max-w-3xl sm:h-[530px] h-[466px] mx-auto rounded-[30px] bg-cover bg-center ${
              handlebg ? "hidden" : ""
            }`}
          >
            {/* LIVE/OFF indicator */}
            <div className="absolute flex items-center bg-[#5A5A5A] w-[226px] h-[40px] rounded-[20px] opacity-40 top-6 sm:top-10 left-1/2 transform -translate-x-1/2">
              <div className="flex items-center ml-2">
                <div className={`${recording ? "bg-[#17BE32]" : "bg-[#BE2117]"} rounded-full w-[34px] h-[34px] mr-4`}></div>
              </div>
              <h3 className="font-bold text-white text-[13px]">{recording ? "LIVE" : "OFF"}</h3>
            </div>

            {/* Record button */}
            <button
              className="absolute bottom-10 left-1/2 transform -translate-x-1/2 bg-[#053676] sm:w-[78px] sm:h-[78px] w-[60px] h-[60px] rounded-full flex items-center justify-center"
              onClick={handleBackground}
            >
              <div className="bg-black sm:w-[70px] sm:h-[70px] w-[48px] h-[48px] rounded-full flex items-center justify-center">
                <div className="sm:w-[58px] sm:h-[58px] w-[34px] h-[34px] bg-[#031c3c] rounded-full"></div>
              </div>
            </button>

            {/* Info text */}
            <h3 className="absolute text-white text-sm sm:text-[16px] text-center top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full">
              Tap the button to start recording
            </h3>
          </div>
        )}

        <div className="w-[350px] h-auto bg-[#A5A5A5] font-[400] p-4 min-h-[5rem] overflow-y-auto mt-4 sm:ml-5 sm:hidden rounded-md shadow">
          {videoURL && (
            <div className="flex flex-col items-start space-y-4">
              <div className="w-full">
                <h1 className="font-bold text-sm mb-1">Submitted Recording</h1>
                <video
                  src={videoURL}
                  controls
                  className="w-full h-[120px] rounded-md shadow-sm object-cover"
                ></video>
              </div>
              <button
                onClick={handleNewSubmission}
                className="bg-blue-500 text-white px-3 py-1.5 rounded text-sm self-start"
              >
                Clear Submission
              </button>
            </div>
          )}

          {isProcessing && (
            <p className="text-sm mt-2 text-gray-800">
              Transcription is being processed...
            </p>
          )}

          {error && (
            <p className="text-sm mt-2 text-red-500">
              {error}
            </p>
          )}

          {isSuccess && transcription && (
            <p className="text-sm mt-2 text-gray-900">
              {transcription.length > 0 ? (
                transcription.map((item, index) => (
                  <span key={index}>{item.prediction} </span>
                ))
              ) : (
                <span>Failed to parse video. Please try again.</span>
              )}
            </p>
          )}
        </div>

        <div className="w-full max-w-3xl hidden md:flex flex-col h-auto bg-[#2A71E81C] rounded-[30px] bg-cover bg-center relative px-4 py-6 ">
          {/* Dropdown */}
          <div className="relative w-full">
            <div
              onClick={toggleDropdown}
              className="w-full h-12 cursor-pointer flex justify-between items-center bg-[#2A71E8] rounded-[31px] px-6 text-white font-bold"
            >
              <p>{selectedOption}</p>
              <SelectorIcon />
            </div>

            {isOpen && (
              <div className="absolute w-full mt-2 z-40 bg-white rounded-[25px] shadow-md py-2">
                {options.map((option) => (
                  <button
                    key={option.label}
                    onClick={() => handleOptionClick(option)}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 rounded-[20px] transition"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Transcription Feedback */}
          <div className="w-full max-w-[600px] mx-auto px-4 py-4 rounded-md">
            {videoURL && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div className="flex flex-col">
                  <h2 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">
                    Submitted Recording
                  </h2>
                  <video
                    src={videoURL}
                    controls
                    className="w-full sm:w-[200px] h-[120px] rounded-md shadow"
                  ></video>
                </div>
                <button
                  onClick={handleNewSubmission}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm"
                >
                  Clear Submission
                </button>
              </div>
            )}
            <h4 className="font-[700] mb-[0.5rem]">Translation</h4>

            {isProcessing && (
              <p className="text-gray-700 text-sm sm:text-base">
                Transcription is being processed...
              </p>
            )}

            {error && (
              <p className="text-red-500 text-sm sm:text-base">
                {error}
              </p>
            )}

              {isSuccess && transcription && (
                <div className="mt-2">
                  <p className="text-sm text-gray-900">
                    {transcription.length > 0 ? (
                      transcription.map((item, index) => (
                        <span key={index}>{item.prediction} </span>
                      ))
                    ) : (
                      <span>Failed to parse video. Please try again.</span>
                    )}
                  </p>

                  <div className="cursor-pointer" onClick={handleRespeak}>
                  <SpeakingIndicator active={transcription.length > 0} />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>

      <div className="border-[1px] w-[1364px] border-[#878787] ml-10 md:block hidden"></div>
      <Bottombar activeBar={"input"} />
    </div>
  );
}

export default SignLanguageToTextSpeech;